import { action, makeObservable, observable, ObservableMap } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { checkIfFileCanBeUploaded } from "@/modules/file/validation";
import { makeUploadUrlParts } from "@/modules/file/upload";
import { uploadFile } from "@/modules/file/upload";
import { MemCommonEditorFileInfo } from "@mem-labs/common-editor";
import { FileDropItemWithPath } from "@/modules/file/dropItems";
import pLimit from "p-limit";

export interface FileUploadProgress {
  filename: string;
  uploadedBytes: number;
  totalBytes: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: Error;
}

export interface FileUploadResult {
  info: MemCommonEditorFileInfo;
  fileItem: FileDropItemWithPath;
  uploadedFileBlobName: string;
  signedUrl: string;
}
export class AppStoreUploadProgressStore extends AppSubStore {
  // Track individual file upload progress
  private fileProgressMap = new ObservableMap<Uuid, FileUploadProgress>();
  private fileResultsMap = new ObservableMap<Uuid, FileUploadResult>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<AppStoreUploadProgressStore, "fileProgressMap" | "fileResultsMap">(this, {
      getFileProgresses: true,
      getFileResults: true,
      getFileProgress: true,
      getFileResult: true,

      fileProgressMap: observable,
      fileResultsMap: observable,

      updateFileProgress: action,
      clearFileProgress: action,
      uploadFileItem: action,
      uploadFileItems: action,
      resetState: action,
    });
  }

  getFileProgress(fileId: Uuid): FileUploadProgress | undefined {
    return this.fileProgressMap.get(fileId);
  }

  getFileResult(fileId: Uuid): FileUploadResult | undefined {
    return this.fileResultsMap.get(fileId);
  }

  getFileProgresses(fileIds: Uuid[]): FileUploadProgress[] {
    return fileIds
      .map(id => {
        return this.getFileProgress(id);
      })
      .filter(Boolean) as FileUploadProgress[];
  }

  getFileResults(fileIds: Uuid[]): FileUploadResult[] {
    return fileIds.map(id => this.getFileResult(id)).filter(Boolean) as FileUploadResult[];
  }

  // File progress methods
  updateFileProgress(fileId: Uuid, progress: Partial<FileUploadProgress>) {
    const currentProgress = this.fileProgressMap.get(fileId) || {
      filename: "",
      uploadedBytes: 0,
      totalBytes: 0,
      status: "pending" as const,
    };

    this.fileProgressMap.set(fileId, {
      ...currentProgress,
      ...progress,
    });
  }

  clearFileProgress(fileId: Uuid) {
    this.fileProgressMap.delete(fileId);
  }

  async uploadFileItem(fileItem: FileDropItemWithPath): Promise<Uuid> {
    const file = await fileItem.getFile();
    const fileId = uuidModule.generate();

    this.updateFileProgress(fileId, {
      filename: fileItem.name,
      status: "pending",
      uploadedBytes: 0,
      totalBytes: file.size,
    });

    const normalizedFileName = normalizeFilename({ fileName: fileItem.name });
    const info = {
      fileId,
      normalizedFileName,
      fileMimeType: fileItem.type,
      encodedFileContent: file.name, // TODO: write a comment explaining this unexpected key/val
      fileSizeBytes: file.size,
    };

    const canBeUploaded = checkIfFileCanBeUploaded({ info });
    if (!canBeUploaded) {
      throw new Error("File can not be uploaded");
    }

    const uploadResult = await uploadFile({
      info,
      blob: file,
      config: {
        // cancelToken: new CancelToken(c => {
        //   this.cancelUpload = c;
        // }),
        onUploadProgress: progress => {
          const status: FileUploadProgress["status"] =
            progress.loaded === progress.total ? "completed" : "pending";
          this.updateFileProgress(fileId, {
            uploadedBytes: progress.loaded,
            totalBytes: progress.total,
            status,
          });
        },
      },
    });

    const { signedUrl } = uploadResult;
    const urlParts = makeUploadUrlParts(signedUrl);
    if (!urlParts.blobName) {
      throw new Error("Invalid signed url");
    }

    this.fileResultsMap.set(fileId, {
      info,
      fileItem,
      uploadedFileBlobName: urlParts.blobName,
      signedUrl,
    });

    return fileId;
  }

  async uploadFileItems(fileItems: FileDropItemWithPath[], concurrencyLimit = 10): Promise<Uuid[]> {
    const uploadLimit = pLimit(concurrencyLimit);
    const promises: Promise<Uuid>[] = fileItems.map(fileItem =>
      uploadLimit(() => this.uploadFileItem(fileItem))
    );

    return Promise.all(promises);
  }

  // Reset all state
  resetState() {
    this.fileProgressMap.clear();
    this.fileResultsMap.clear();
  }
}
